import { Mediator, app, payload, eData } from "modular-openscriptjs";
import {
  eParse,
  inform,
  processStart,
  processEnd,
  oneOf,
  authenticated,
  approved,
  empty,
  lastOf,
  afterAuth,
  redirect,
  requireAuth,
} from "../helpers";
import { analyze } from "../analytics";
import { STRINGS, api, gc, uc, dc } from "../declarations";
import { routes } from "../backendRoutes";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import {
    activateEchoListeners,
    deactivateEchoListeners,
} from "../socket-config";

const $e = app("$e");

export class User extends Mediator {
  $$user = {
    updated: async (ed, event) => this.getUser(),

    authenticated_loggedOut: async (ed, event) => {
      if (event == $e.user.loggedOut && authenticated() == true) {
        return app("router").to(`/`);
      }

      if (event == $e.user.authenticated) {
        if (Notification?.permission == "granted") {
          this.send($e.system.has.notificationPermission);
        }

        activateEchoListeners();

        if (authenticated() == true) {
          setTimeout(() => {
            if (app("router").qs.has("su")) {
              analyze("sign_up", {
                method: app("router").qs.get("su"),
              });
            }

            if (app("router").qs.has("l")) {
              analyze("login", {
                method: app("router").qs.get("l"),
              });
            }
          }, 1000);
        }
      } else {
        deactivateEchoListeners();
      }
    },

    has: {
      information: async (ed, event) => {
        // cleaned up location streaming
      },

      fcmToken: async (ed, event) => {
        if (!gc.auth.value) return;

        this.updateFcmToken(ed, event);
      },

      followed_unfollowed_blocked_unblocked: () => {
        this.send($e.user.needs.information);
      },
    },

    needs: {
      update: async (ed, event) => {
        this.updateUserProfile(ed, event);
      },

      fcmTokenUpdate: (ed, event) => {
        // this.updateFcmToken(ed, event);
      },

      quickUpdate: async (ed, event) => {
        this.broadcast($e.user.needs.profileModal, payload());
      },

      password: {
        link: async (ed, event) => this.getPasswordLink(ed, event),

        reset: async (ed, event) => this.resetPassword(ed, event),

        change: async (ed, event) => {
          this.changePassword(ed, event);
        },
      },

      referFriend: async (ed, event) => {
        if (!requireAuth("Sign in to get your referral link")) return;
        this.refer(ed, event);
      },

      // $e.user.needs.authentication is handled by LoginModal
      // (components/Modals/LoginModal.js), which pops up the sign-in
      // options over the current page instead of navigating to /login.

      addressForm: (ed, event) =>
        this.send($e.deliveryAddress.needs.addressForm, ed),

      information: (ed, event) => {
        this.getUser();
      },

      otherUserInformation: (ed, event) => {
        this.getOtherUser(ed, event);
      },

      toFollow: (ed, event) => {
        this.followUser(ed, event);
      },

      toBlock: (ed, event) => {
        this.block(ed, event);
      },

      toUnfollow: (ed, event) => {
        this.unfollowUser(ed, event);
      },

      toUnblock: (ed, event) => {
        this.unblock(ed, event);
      },

      to: {
        change: {
          onlineStatus: (ed, event) => this.changeOnlineStatus(ed, event),
        },

        orderViaChat: (ed, event) => this.initiateChatOrder(ed, event),
      },
    },

    profilePicture: {
      needs: {
        change: (ed, event) => {
          this.send($e.user.profilePicture.needs.pictureForm, ed);
        },
        storage: (ed, event) => {
          this.uploadProfilePicture(ed, event);
        },
      },
      changed: () => this.send($e.user.needs.information),
    },
  };

  $$business = {
    needs: {
      additionalPhoneNumbers: (ed, event) =>
        this.send($e.business.needs.phoneNumbersForm, ed),

      schedule: (ed, event) => this.send($e.business.needs.scheduleForm, ed),
      location: (ed, event) => {
        this.send(
          $e.location.is.needed,
          payload(
            { current: gc.user?.shop_location },
            { requester: "user-mediator" },
          ),
        );
      },
      to: {
        storePhoneNumbers: (ed, event) =>
          this.saveAdditionalPhoneNumbers(ed, event),
        storeSchedule: (ed, event) =>
          this.updateBusinessOperationDays(ed, event),
      },
    },
  };

  $$app = {
    loaded_reloaded: async (ed, event) => {
      if (!authenticated()) return;

      this.getUser();
      this.broadcast($e.user.needs.fcmTokenUpdate);
    },
  };

  $$system = {
    needs: {
      notificationPermission: async (ed, event) => {
        if (!("Notification" in window)) return this;

        if (Notification.permission === "granted") {
          this.send($e.system.has.notificationPermission);
        } else if (Notification.permission !== "denied") {
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              this.send($e.system.has.notificationPermission);
            }
          });
        }
        return this;
      },
    },
    booted: () => {
      if (this.shouldPromptNotificationPermission()) {
        setTimeout(() => {
          this.send($e.app.needs.to.askForPermission);
        }, 60 * 1000);
      }
    },
  };

  async updateFcmToken(ed, event) {
    let { message } = eParse(ed);
    await api.put(
      `${routes.users}/fcm-token`,
      { token: message.token },
      { silent: true },
    );
  }

  async updateBusinessOperationDays(ed, event) {
    if (!approved(event, ed, "You want to update your operation days")) return;

    let { schedule } = eParse(ed).message;

    let res = await api.post(`${routes.businesses}/business-operation-days`, {
      schedule,
    });

    if (!res) {
      return this.send($e.business.has.scheduleStorageError, ed);
    }

    inform(res.message, STRINGS.SUCCESS);

    return this.send($e.business.scheduleStored, ed);
  }

  async updateBusinessAddressInfo(ed, event) {
    let data = eParse(ed);

    let res = await api.post("/shop-locations/save", data.message);
    if (!res) return;
    inform(`${res.message}`, STRINGS.SUCCESS);
  }

  async saveAdditionalPhoneNumbers(ed, event) {
    if (!approved(event, ed, "You want to update your contact information"))
      return;

    let { numbers } = eParse(ed).message;

    if (empty(numbers)) numbers = [];

    let res = await api.post(`${routes.businesses}/additional-phone-numbers`, {
      additional_phone_numbers: numbers,
    });

    if (!res) {
      return this.send($e.business.has.numbersStorageError, ed);
    }

    inform(res.message, STRINGS.SUCCESS);

    gc.user.value.additional_numbers = res.data.additional_numbers;

    return this.send($e.business.numbersStored, ed);
  }

  async updateUserProfile(ed, event) {
    if (!approved(event, ed, "You want to update your information")) return;

    let data = eParse(ed);
    let required = { firstName: true, lastName: true, phone: true };

    this.broadcast($e.app.indicator.process.start);

    for (let k in data.message.getAll()) {
      let d = `${data.message.get(k)}`;
      d = d.replace(/\[\n\t\r\v]/g, " ");

      data.message.put(k, d);

      if (!(k in required)) continue;

      if (data.message[k].length === 0) {
        inform(`${k} is required`, STRINGS.ERROR);
        this.broadcast($e.app.indicator.process.end);
        return;
      }
    }

    if ("phone" in data.message) {
      if (!/^\+?\d{9,12}$/g.test(data.message.phone)) {
        inform("Please enter a valid phone number", STRINGS.ERROR);

        this.broadcast($e.app.indicator.process.end);
        return;
      }

      data.message.phone = data.message.phone.replace("+", "");
    }

    if ("photo" in data.message) {
      photo = data.message.photo;
      delete data.message.photo;
    }

    let res = await api.put("/user/profile-information", data.message);

    this.broadcast($e.app.indicator.process.end);

    if (!res) return;

    inform(res.message || "Profile Updated Successfully", STRINGS.SUCCESS);

    return this.broadcast($e.user.updated, ed);
  }

  async getUser() {
    let res = await api.get("/users/current", {}, { silent: true });

    if (!res) {
      gc.user.value = {};
      uc.roles.value = {};
      uc.permissions.value = {};
      return;
    }

    let user = res.data.user ?? {};
    let roles = {};
    let permissions = {};

    let userRoles = oneOf(user.current_roles, []);
    let userPermissions = oneOf(user.current_permissions, []);

    let mx = Math.max(userRoles.length, userPermissions.length);

    for (let i = 0; i < mx; i++) {
      if (userRoles[i]) roles[userRoles[i]] = true;
      if (userPermissions[i]) permissions[userPermissions[i]] = true;
    }

    uc.roles.value = roles;
    uc.permissions.value = permissions;

    gc.user.value = user;

    this.send($e.user.has.information);
  }

  async changePassword(ed, event) {
    if (!authenticated()) {
      inform("You are not authenticated", STRINGS.ERROR);

      this.broadcast($e.user.needs.authentication);

      app("router").to("/");
    }

    if (!approved(event, ed, "You want to change your password")) return;

    this.broadcast($e.app.indicator.process.start);

    let data = eParse(ed);

    let {
      newPassword: password,
      password: current_password,
      confPassword: password_confirmation,
    } = data.message;

    if (password !== password_confirmation) {
      inform("Password Mismatch", STRINGS.WARNING);

      this.broadcast($e.app.indicator.process.end);

      return this.broadcast($e.user.has.updateError);
    }

    let res = await api.put("/user/password", {
      password,
      current_password,
      password_confirmation,
    });

    this.broadcast($e.app.indicator.process.end);

    if (!res) return;

    inform(res.message || "Password Changed Successfully", STRINGS.SUCCESS);

    return this.broadcast($e.user.updated, eData(ed));
  }

  async signUp(ed, event) {
    this.broadcast($e.app.indicator.process.start);

    let { message } = eParse(ed);

    if (message.password !== message.password_confirmation) {
      this.broadcast($e.app.indicator.process.end);
      this.broadcast($e.user.has.registrationError);

      return inform("Password Mismatch", STRINGS.WARNING);
    }

    let res = await api.post("/register", message);

    if (!res) {
      this.broadcast($e.user.has.registrationError);
      return this.broadcast($e.app.indicator.process.end);
    }

    inform(res.message || "You were signed up successfully. Logging you In", STRINGS.SUCCESS);

    this.broadcast($e.user.needs.authCheck);
    this.broadcast($e.app.indicator.process.end);
    this.broadcast($e.user.authenticated);

    setTimeout(() => {
      this.send($e.system.needs.reload);
    }, 500);

    app("router").to("/");
  }

  async getPasswordLink(ed, event) {
    let { message } = eParse(ed);

    this.broadcast($e.app.indicator.process.start);

    if (message?.email?.length < 1) {
      this.broadcast($e.app.indicator.process.end);
      return inform("Please Enter an Email", STRINGS.ERROR);
    }

    let res = await api.post("/forgot-password", message);

    this.broadcast($e.app.indicator.process.end);

    if (!res) return;

    inform(res.message || "We've sent you a password reset link", STRINGS.SUCCESS);
  }

  async resetPassword(ed, event) {
    let { message } = eParse(ed);
    this.broadcast($e.app.indicator.process.start);

    if (message.password !== message.password_confirmation) {
      this.broadcast($e.app.indicator.process.end);
      return inform("Password Mismatch", STRINGS.WARNING);
    }

    let res = await api.post("/reset-password", message);

    if (!res) {
      return this.broadcast($e.app.indicator.process.end);
    }

    inform(res.message || "your password was successfully reset", STRINGS.SUCCESS);

    afterAuth(() => app("router").to("/"));
    this.broadcast($e.user.needs.authentication);
  }

  async refer() {
    let content = {
      url: gc?.user?.referral_link,
      title: "Refer A Friend",
      text: "Order Anything on Carata",
    };

    this.send($e.user.needs.referFriendMedia, payload({ content }));
  }

  async uploadProfilePicture(ed, event) {
    if (!approved(event, ed, "You want to change your profile picture?")) {
      return;
    }

    processStart({ title: "Uploading" });

    const fileObj = dc.userProfileImage;
    const arrayBuffer = await fileObj.arrayBuffer();
    const robustBlob = new Blob([arrayBuffer], { type: fileObj.type });

    let formData = new FormData();
    formData.append("image", robustBlob);
    formData.append(
      "crop_using",
      JSON.stringify(eParse(ed).message.get("crop_data")),
    );

    let res = await api.post(`${routes.users}/profile-picture`, formData, {
      fetch_using: "browser",
      "remove-content-type": true,
    });

    delete dc["userProfileImage"];
    processEnd();

    if (!res) {
      return this.send($e.user.profilePicture.has.storageError, ed);
    }

    inform(res.message || "Profile picture updated successfully", STRINGS.SUCCESS);
    if (res.data && res.data.user) {
      gc.user.value = res.data.user;
    }

    return this.send($e.user.profilePicture.changed, ed);
  }

  async getOtherUser(ed, event) {
    let data = eParse(ed);
    let id = data.message.get("id");

    let res = await api.get(`${routes.users}/${id}`);

    if (!res) {
      uc.userBeingViewed.value = {};
      return;
    }

    this.setViewedUser(res.data.user);
  }

  async followUser(ed, event) {
    let data = eParse(ed);
    let user = data.message.get("user");

    let res = await api.post(`${routes.users}/follows/${user?.id}`);

    if (!res) {
      return;
    }

    data.message.follow = res.data.follow;

    inform(res.message, STRINGS.SUCCESS);

    if (
      data.message.has("resetViewedUser") &&
      app("router").is("user.view-profile") &&
      app("router").params.user == user.id
    ) {
      this.send($e.user.needs.otherUserInformation, payload({ id: user.id }));
    }

    this.send($e.user.has.followed, data.encode());
  }

  async unfollowUser(ed, event) {
    let data = eParse(ed);
    let follow = data.message.get("follow");

    let res = await api.delete(`${routes.follows}/${follow?.id}`);

    if (!res) {
      return;
    }

    inform(res.message, STRINGS.SUCCESS);

    if (
      data.message.has("resetViewedUser") &&
      app("router").is("user.view-profile") &&
      app("router").params.user == follow.user_id
    ) {
      this.send(
        $e.user.needs.otherUserInformation,
        payload({ id: follow.user_id }),
      );
    }

    this.send($e.user.has.unfollowed, ed);
  }

  async block(ed, event) {
    if (!approved(event, ed, "You want to block this user")) return;

    let data = eParse(ed);
    let follow = data.message.get("follow");

    let res = await api.put(`${routes.follows}/${follow?.id}/block`);

    if (!res) {
      return;
    }

    inform(res.message, STRINGS.SUCCESS);

    this.send($e.user.has.blocked, ed);

    app("router").to("user.view-profile", { user: "current" });
  }

  async unblock(ed, event) {
    if (!approved(event, ed, "You want to unblock this user")) return;

    let data = eParse(ed);
    let follow = data.message.get("follow");

    let res = await api.put(`${routes.follows}/${follow?.id}/unblock`);

    if (!res) {
      return;
    }
    this.send($e.user.has.unblocked, ed);
    inform(res.message, STRINGS.SUCCESS);
  }

  async changeOnlineStatus(ed, event) {
    let data = eParse(ed);
    let status = data.message.get("status");

    if (!approved(event, ed, "You really want to go " + status)) return;

    let res = await api.post(`${routes.users}/deliverer-profile/is/${status}`);

    if (!res) {
      return;
    }

    inform(res.message, STRINGS.SUCCESS);

    this.getUser();

    this.send($e.user.has.changed.onlineStatus, data.encode());
  }

  async setViewedUser(user) {
    uc.userBeingViewed.value = user;
  }

  async initiateChatOrder(ed, event) {
    const data = eParse(ed);

    let orderProductId = data.message.get("order_product_id");

    let res = await api.post("/chat-initiated-orders", {
      order_product_id: orderProductId,
    });

    if (!res) {
      return this.send($e.user.has.chatInitiatedOrderError, ed);
    }

    this.send($e.user.has.initiatedChatOrder, ed);

    let phone = res.data.phone;
    let product = res.data.product;

    let link = `${window.location.origin}/app/products/${product.id}/view`;
    let text = `Hi, I am interested in this product you sell on Carata.
		
*Product: ${product.name}*
*Product link:* ${link}

After we agree, please use Carata pickup and dropoff to deliver it to me.
`;
    let url = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURI(
      text,
    )}`;

    let a = h.a({ href: url, target: "_self", parent: document.body });
    a.click();
  }

  async shouldPromptNotificationPermission() {
    if (Capacitor.isNativePlatform()) {
      const status = await PushNotifications.checkPermissions();
      if (status.receive === "granted") return false;
    } else {
      if (
        Notification.permission == "granted" ||
        Notification.permission == "denied"
      )
        return false;
    }

    let lastAsked = localStorage.getItem("np-asked-at");

    if (!lastAsked) {
      return true;
    }

    let date = new Date(lastAsked);
    let now = new Date();
    let difference = now.getTime() - date.getTime();

    if (difference >= dc.permWaitInterval) return true;

    return false;
  }
}

